/**
 * Coaching Service
 * Handles all coaching session-related API calls
 */

import { apiClient } from "@/lib/api-client"

export type CoachingFormat = "IN_PERSON" | "VIRTUAL" | "HYBRID"
export type ParticipationFee = "Free" | "Paid"
export type CalendarIntegration = "none" | "google" | "outlook" | "calendly"

export interface TimeSlot {
  id: string
  dayOfWeek: "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN"
  startTime: string
  endTime: string
  timezone: string
}

export interface CreateCoachingRequest {
  title: string
  format: CoachingFormat
  duration: number // in minutes
  address?: string
  meetingLink?: string
  participationFee: ParticipationFee
  price?: number
  feeDetails?: string
  calendarIntegration?: CalendarIntegration
  availableTimeSlots?: TimeSlot[]
  description: string
  whatToBring?: string
  whatYoullExperience?: string
  intention?: string
  websiteOrSocialMedia?: string
  tags: string[]
  privacySettings: {
    visibility: string[]
  }
  files?: File[]
  coverImageIndex?: number
  isDraft?: boolean
}

export interface CoachingResponse {
  id: string
  title: string
  format: CoachingFormat
  createdAt: Date
}

export interface CoachingSessionMediaItem {
  id: string
  isCover: boolean
  order: number
  upload: {
    id: string
    filePath: string
    fileName: string
  }
}

export interface CoachingSessionDetail {
  id: string
  title: string
  description?: string | null
  format: CoachingFormat
  duration: number
  streetAddress?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  lat?: number | null
  lng?: number | null
  meetingLink?: string | null
  participationFee: ParticipationFee
  price: number | null
  feeDetails?: string | null
  calendarIntegration?: string | null
  availableTimeSlots?: TimeSlot[] | null
  whatToBring?: string | null
  experienceDetails?: string | null
  intention?: string | null
  websiteLink?: string | null
  visibility?: string[]
  isLive: boolean
  isOwner?: boolean
  isSaved?: boolean
  createdBy: string
  createdAt: string
  coverImage?: string | null
  media?: CoachingSessionMediaItem[]
  tags?: Array<{ id: string; tagText: string }> | string[]
  creator?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl?: string | null
    followers?: number
  } | null
}

/**
 * Create a new coaching session
 * Supports multipart/form-data for file uploads
 */
export async function createCoachingSession(
  data: CreateCoachingRequest,
): Promise<CoachingResponse> {
  const formData = new FormData()

  // Basic info
  formData.append("title", data.title)
  formData.append("format", data.format)
  formData.append("duration", data.duration.toString())

  // Location
  if (data.address) formData.append("address", data.address)
  if (data.meetingLink) formData.append("meetingLink", data.meetingLink)

  // Pricing
  formData.append("participationFee", data.participationFee)
  if (data.price !== undefined) formData.append("price", data.price.toString())
  if (data.feeDetails) formData.append("feeDetails", data.feeDetails)

  // Calendar (as JSON)
  if (data.calendarIntegration) {
    formData.append("calendarIntegration", data.calendarIntegration)
  }
  if (data.availableTimeSlots) {
    formData.append("availableTimeSlots", JSON.stringify(data.availableTimeSlots))
  }

  // Details
  if (data.description) formData.append("description", data.description)
  if (data.whatToBring) formData.append("whatToBring", data.whatToBring)
  if (data.whatYoullExperience) formData.append("whatYoullExperience", data.whatYoullExperience)
  if (data.intention) formData.append("intention", data.intention)
  if (data.websiteOrSocialMedia) formData.append("websiteOrSocialMedia", data.websiteOrSocialMedia)

  // Tags
  if (data.tags && data.tags.length > 0) {
    formData.append("tags", JSON.stringify({ add: data.tags }))
  }

  // Privacy
  formData.append("visibility", JSON.stringify(data.privacySettings.visibility))

  // Cover index
  if (data.coverImageIndex !== undefined) {
    formData.append("cover_image_index", data.coverImageIndex.toString())
  }

  // Draft flag
  if (data.isDraft !== undefined) {
    formData.append("isDraft", String(data.isDraft))
  }

  // Files
  if (data.files && data.files.length > 0) {
    for (const file of data.files) {
      formData.append("files", file)
    }
  }

  const response = await apiClient.post<{
    success: boolean
    data: CoachingResponse
    message: string
  }>("/api/coaching-sessions", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

export async function getCoachingSessionById(id: string): Promise<CoachingSessionDetail> {
  const response = await apiClient.get<{
    success: boolean
    data: CoachingSessionDetail
    message: string
  }>(`/api/coaching-sessions/${id}`)
  return response.data.data
}

export interface UpdateCoachingRequest {
  title?: string
  format?: CoachingFormat
  duration?: number
  address?: string
  meetingLink?: string
  participationFee?: ParticipationFee
  price?: number
  feeDetails?: string
  calendarIntegration?: CalendarIntegration
  availableTimeSlots?: TimeSlot[]
  description?: string
  whatToBring?: string
  whatYoullExperience?: string
  intention?: string
  websiteOrSocialMedia?: string
  tags?: string[]
  privacySettings?: { visibility: string[] }
  files?: File[]
  coverImageIndex?: number
  isDraft?: boolean
}

export async function updateCoachingSession(
  id: string,
  data: UpdateCoachingRequest,
): Promise<CoachingResponse> {
  const formData = new FormData()

  if (data.title !== undefined) formData.append("title", data.title)
  if (data.format !== undefined) formData.append("format", data.format)
  if (data.duration !== undefined) formData.append("duration", data.duration.toString())

  if (data.address !== undefined) formData.append("address", data.address)
  if (data.meetingLink !== undefined) formData.append("meetingLink", data.meetingLink)

  if (data.participationFee !== undefined)
    formData.append("participationFee", data.participationFee)
  if (data.price !== undefined) formData.append("price", data.price.toString())
  if (data.feeDetails !== undefined) formData.append("feeDetails", data.feeDetails)

  if (data.calendarIntegration !== undefined) {
    formData.append("calendarIntegration", data.calendarIntegration)
  }
  if (data.availableTimeSlots !== undefined) {
    formData.append("availableTimeSlots", JSON.stringify(data.availableTimeSlots))
  }

  if (data.description !== undefined) formData.append("description", data.description)
  if (data.whatToBring !== undefined) formData.append("whatToBring", data.whatToBring)
  if (data.whatYoullExperience !== undefined)
    formData.append("whatYoullExperience", data.whatYoullExperience)
  if (data.intention !== undefined) formData.append("intention", data.intention)
  if (data.websiteOrSocialMedia !== undefined)
    formData.append("websiteOrSocialMedia", data.websiteOrSocialMedia)

  if (data.tags !== undefined && data.tags.length > 0) {
    formData.append("tags", JSON.stringify({ add: data.tags }))
  }

  if (data.privacySettings !== undefined) {
    formData.append("visibility", JSON.stringify(data.privacySettings.visibility))
  }

  if (data.coverImageIndex !== undefined) {
    formData.append("cover_image_index", data.coverImageIndex.toString())
  }

  if (data.isDraft !== undefined) {
    formData.append("isDraft", String(data.isDraft))
  }

  if (data.files && data.files.length > 0) {
    for (const file of data.files) {
      formData.append("files", file)
    }
  }

  const response = await apiClient.patch<{
    success: boolean
    data: CoachingResponse
    message: string
  }>(`/api/coaching-sessions/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

export interface CoachingSessionListItem {
  id: string
  title: string
  description?: string | null
  format: CoachingFormat
  duration: number
  participationFee: ParticipationFee
  price: number | null
  tags?: string[]
  isLive: boolean
  isOwner?: boolean
  isSaved?: boolean
  createdBy: string
  createdAt: string
  coverImage?: string | null
  websiteLink?: string | null
  streetAddress?: string | null
  durationType?: "minutes" | "hours" | "days"
  stats?: {
    views: number
    bookings: number
    completed: number
    earning: number
  } | null
  creator?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl?: string | null
    followers?: number
  } | null
}

export interface CoachingSessionListResponse {
  sessions: CoachingSessionListItem[]
  coachingCount: number
  bookingCount: number
  total: number
  page: number
  totalPages: number
  hasMore: boolean
}

export async function listMyCoachingSessions(params?: {
  userId?: string
  page?: number
  limit?: number
  search?: string
}): Promise<CoachingSessionListResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: CoachingSessionListResponse
    message: string
  }>("/api/coaching-sessions", { params })
  const data = response.data.data
  return {
    ...data,
    sessions: data.sessions.map(session => ({
      ...session,
      tags: session.tags?.map(tag =>
        typeof tag === "object" && tag !== null && "tagText" in tag
          ? (tag as { tagText: string }).tagText
          : (tag as string),
      ),
    })),
  }
}

/**
 * List coaching sessions publicly (for browse page)
 * Returns all live coaching sessions without user filtering
 */
export async function listCoachingSessionsPublic(params?: {
  page?: number
  limit?: number
  search?: string
}): Promise<CoachingSessionListResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: CoachingSessionListResponse
    message: string
  }>("/api/coaching-sessions", { params })
  const data = response.data.data
  return {
    ...data,
    sessions: data.sessions.map(session => ({
      ...session,
      tags: session.tags?.map(tag =>
        typeof tag === "object" && tag !== null && "tagText" in tag
          ? (tag as { tagText: string }).tagText
          : (tag as string),
      ),
    })),
  }
}

/**
 * Delete a coaching session
 * @param id - The ID of the coaching session to delete
 */
export async function deleteCoachingSession(id: string): Promise<void> {
  await apiClient.delete<{
    success: boolean
    data: null
    message: string
  }>(`/api/coaching-sessions/${id}`)
}

// ========================================
// Coaching Booking API Functions
// ========================================

export interface CoachingAvailabilitySlot {
  id: string
  date: string
  startTime: string
  endTime: string
  isBooked: boolean
}

export interface CoachingBookingResult {
  booking: {
    id: string
    status: string
    date: string
    startTime: string
    endTime: string
    coachingSessionId: string
    coachingSessionTitle: string
    expertId: string
    isPaid: boolean
    fee: number | null
    feeCurrency: string | null
  }
  checkoutUrl?: string
  transactionId?: string
}

export interface TimeRequestData {
  coachingSessionId: string
  preferredDates: string[]
  preferredTimes: string[]
  timezone: string
  message?: string
}

/**
 * Get available time slots for a coaching session (by month/year)
 */
export async function getCoachingAvailability(
  coachingSessionId: string,
  month: number,
  year: number,
): Promise<CoachingAvailabilitySlot[]> {
  const response = await apiClient.get<{
    success: boolean
    data: CoachingAvailabilitySlot[]
    message: string
  }>(`/api/coaching-sessions/${coachingSessionId}/availability`, {
    params: { month, year },
  })
  return response.data.data
}

/**
 * Book a coaching session
 */
export async function bookCoachingSession(
  coachingSessionId: string,
  availabilityId: string,
  message?: string,
  options?: { successUrl?: string; cancelUrl?: string },
): Promise<CoachingBookingResult> {
  const response = await apiClient.post<{
    success: boolean
    data: CoachingBookingResult
    message: string
  }>(`/api/coaching-sessions/${coachingSessionId}/book`, {
    availabilityId,
    message,
    ...(options?.successUrl ? { successUrl: options.successUrl } : {}),
    ...(options?.cancelUrl ? { cancelUrl: options.cancelUrl } : {}),
  })
  return response.data.data
}

/**
 * Request a custom time for a coaching session
 */
export async function requestCoachingTime(data: TimeRequestData): Promise<void> {
  await apiClient.post(`/api/coaching-sessions/${data.coachingSessionId}/request-time`, {
    preferredDates: data.preferredDates,
    preferredTimes: data.preferredTimes,
    timezone: data.timezone,
    message: data.message,
  })
}

/**
 * Cancel a coaching booking
 */
export async function cancelCoachingBooking(bookingId: string): Promise<void> {
  await apiClient.post(`/api/coaching-sessions/bookings/${bookingId}/cancel`)
}

/**
 * Add coaching booking to Google Calendar
 * Returns the Google Calendar event link
 */
export async function addCoachingToCalendar(bookingId: string): Promise<{
  calendarEventId: string
  googleEventId: string
  htmlLink: string
}> {
  const response = await apiClient.post<{
    success: boolean
    data: {
      calendarEventId: string
      googleEventId: string
      htmlLink: string
    }
    message: string
  }>(`/api/coaching-sessions/bookings/${bookingId}/add-to-calendar`)
  return response.data.data
}

/**
 * Increment view count for a coaching session
 * Called when user clicks "Book a Session" button
 */
export async function incrementCoachingViewCount(sessionId: string): Promise<{
  viewCount: number
}> {
  const response = await apiClient.put<{
    success: boolean
    data: { viewCount: number }
    message: string
  }>(`/api/coaching-sessions/${sessionId}/views`)
  return response.data.data
}
